import React, { FC, ReactElement, useEffect, useState } from "react";
import { toast } from "react-toastify";
import ActivityFrame from "../../components/activity-frame/activity-frame";
import AssignedTab from "../../components/assignWizard/AssignedTab";
import AssignModalTwo from "../../components/assignWizard/AssignModal";
import { Button } from "../../components/button/Button";
import { useDialog } from "../../components/dialog/DialogContext";
import Tabs from "../../components/tabs/tabs";
import { ACCESS_TYPES } from "../../constants/access-constants";
import { useLocale } from "../../contexts/localeContext";
import { TranslationKeys } from "../../contexts/TranslationKeys";
import AccessGroupColumns from "../../models/ColumnDefinitions/AccessGroupColumns";
import AccessProfileColumn from "../../models/ColumnDefinitions/AccessProfileColumn";
import { ListQuery } from "../../models/ListQuery";
import { Lookup } from "../../models/Lookup";
import LookupTypes from "../../models/LookupTypes";
import AccessAreaSearch from "../../models/search/AccessAreaSearch";
import AccessGroupSearch from "../../models/search/AccessGroupSearch";
import AccessProfileSearch from "../../models/search/AccessProfileSearch";
import AccessTimePatternSearch from "../../models/search/AccessTimePatternSearch";
import SiteSearch from "../../models/search/SiteSearch";
import { TableColumn } from "../../models/table/TableColumn";
import { TabSummary } from "../../models/TabSummary";
import { extendColumnDefinition, extendLookups, extendSearchColumn, hideColumns, removeColumns } from "../../models/utilities/columnHelper";
import { getAccessAreaNamesWithPagination } from "../../services/accessAreaService";
import { getAccessProfileAccessGroupsById } from "../../services/accessGroupProfileService";
import { getAccessGroupsForPerson, getAccessGroupsNamesWithPagination, getAllAccessGroups } from "../../services/accessGroupService";
import { getAccessProfilesForPerson, getAccessProfilesNamesWithPagination, getAllAccessProfiles } from "../../services/accessProfileService";
import { getAccessTimePatternNames } from "../../services/accessTimePatternService";
import { getAccessProfileAndGroupCounts } from "../../services/enrolmentService";
import { assignAndUnassignMasterGroupProfile } from "../../services/masterGroupProfileService";
import { assignUnassignMasterGroup } from "../../services/masterGroupService";
import { getSiteNamesWithPagination } from "../../services/siteService";
import { getErrorMessage, showError } from "../../services/utilities/ServiceError";
import { getDayNames } from "../../utilities/DateUtils";
import { appendFilter } from "../../utilities/filters";
import { PaginationModel } from "@/models/Pagination";
import { LICENSE_APERIO_OSS } from "../../constants/license-constants";
import { useGlobalState } from "../../Providers";

interface UserDetailsAccessTabTwoProps {
    masterId: string;
}

type EntityType = "ACCESS_GROUP" | "ACCESS_PROFILE" | "FLOOR_GROUP" | "OSS_GROUP";

const UserDetailsAccessTabTwo: FC<UserDetailsAccessTabTwoProps> = ({ masterId }) => {
    const { buildInfo } = useGlobalState() ?? {};
    const isLicensedForAperioOss = buildInfo?.licenceIds?.includes(LICENSE_APERIO_OSS);
    const dialog = useDialog();
    const { translate } = useLocale();
    const [busy, setBusy] = useState<boolean>(false);
    const initialTabs: TabSummary[] = [
        { label: translate.byKey(TranslationKeys.accessGroups), badge: 0, showInitial: true, isActive: true },
        { label: translate.byKey(TranslationKeys.floorGroups), badge: 0, showInitial: false, isActive: true },
        ...(isLicensedForAperioOss ? [{ label: translate.byKey(TranslationKeys.ossGroups), badge: 0, showInitial: false, isActive: true }] : []),
        { label: translate.byKey(TranslationKeys.accessProfiles), badge: 0, showInitial: true, isActive: true },
    ];
    const [tabs, setTabs] = useState<TabSummary[]>(initialTabs);
    const [selectedTab, setSelectedTab] = useState(0);
    const [showManageModal, setShowManageModal] = useState<boolean>(false);
    const [beingAssigned, setBeingAssigned] = useState<EntityType>("ACCESS_GROUP");

    useEffect(() => {
	    fetchAccessProfileAndGroupCounts(masterId);
    }, [masterId]);

    const fetchAccessProfileAndGroupCounts = async (masterId: any) => {
        try {
            const counts = await getAccessProfileAndGroupCounts(masterId);
            const tabIndexProfile = isLicensedForAperioOss ? 3 : 2;

            setTabs(currentTabs =>
                updateTabBadgeTotal(tabIndexProfile, counts.accessProfileCount,
                    updateTabBadgeTotal(2, counts.ossGroupCount,
                        updateTabBadgeTotal(1, counts.floorGroupCount ?? 0,
                            updateTabBadgeTotal(0, counts.accessGroupCount ?? 0, currentTabs)
                        )
                    )
                )
            );

        } catch (error) {
            showError(error, toast.warn);
        }
    };

    const updateTabBadgeTotal = (tabIndex: number, totalCount: number, currentTabs: TabSummary[]) => {
        const newTabs = [...currentTabs];
        newTabs[tabIndex].badge = totalCount;
        return newTabs;
    };

    const AccessGroupLookup: Lookup[] = [
        {
            name: LookupTypes.site,
            values: [],
            dataMethod: getSiteNamesWithPagination,
            searchAccessor: SiteSearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.accessTimePattern,
            values: [],
            dataMethod: getAccessTimePatternNames,
            searchAccessor: AccessTimePatternSearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.area,
            values: [],
            dataMethod: (listQuery?: ListQuery) => getAccessAreaNamesWithPagination(null, listQuery),
            searchAccessor: AccessAreaSearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.days,
            values: getDayNames(translate)
        }
    ]

    const extendAssignedAccessGroupsColumns = (): TableColumn[] => {
        const columnsToExtend = AccessGroupColumns.filter(column => column.accessor !== "days");
        const extendedColumns = extendColumnDefinition(columnsToExtend, 'accessGroup');
        const daysColumn = AccessGroupColumns.find(column => column.accessor === "days");
        return daysColumn ? [...extendedColumns, daysColumn] : extendedColumns;
    };

    const tabContent: ReactElement[] = [
            <AssignedTab
                key={"AccessGroup-tab"}
                column={extendAssignedAccessGroupsColumns()}
                searchColumn={extendSearchColumn(AccessGroupSearch, "accessGroup")}
                dataMethod={(listParameters?: ListQuery) =>
                    getAccessGroupsForPerson(translate, {
                        ...listParameters,
                        filters: appendFilter(
                            masterId,
                            "Master",
                            "master.id",
                            appendFilter(
                                ACCESS_TYPES.ACCESS_GROUP,
                                "Type",
                                "accessGroup.agType",
                                listParameters?.filters ?? []
                            )
                        ),
                    })
                }
                id={masterId}
                lookup={extendLookups(AccessGroupLookup, 'accessGroup')}
                title={translate.byKey(TranslationKeys.assignedAccessGroups)}>
                <Button variant="submit" shape="rounded" className="py-1 px-5" onClick={() => setShowManageModal(true)}>{translate.byKey(TranslationKeys.manage)}</Button>
            </AssignedTab>,
            <AssignedTab
                key={"floorGroup-tab"}
                column={extendAssignedAccessGroupsColumns()}
                searchColumn={extendSearchColumn(AccessGroupSearch, "accessGroup")}
                dataMethod={(listParameters?: ListQuery) =>
                    getAccessGroupsForPerson(translate, {
                        ...listParameters,
                        filters: appendFilter(
                            masterId,
                            "Master",
                            "master.id",
                            appendFilter(
                                ACCESS_TYPES.FLOOR_GROUP,
                                "Type",
                                "accessGroup.agType",
                                listParameters?.filters ?? []
                            )
                        ),
                    })
                }
                id={masterId}
                lookup={extendLookups(AccessGroupLookup, 'accessGroup')}
                title={translate.byKey(TranslationKeys.assignedFloorGroups)}>
                <Button variant="submit" shape="rounded" className="py-1 px-5" onClick={() => setShowManageModal(true)}>{translate.byKey(TranslationKeys.manage)}</Button>
            </AssignedTab>,
        ...(isLicensedForAperioOss ? [
            <AssignedTab
                key={"ossGroup-tab"}
                column={removeColumns(extendAssignedAccessGroupsColumns(), ['area'])}
                searchColumn={extendSearchColumn(AccessGroupSearch, "accessGroup")}
                dataMethod={(listParameters?: ListQuery) =>
                    getAccessGroupsForPerson(translate, {
                        ...listParameters,
                        filters: appendFilter(
                            masterId,
                            "Master",
                            "master.id",
                            appendFilter(
                                ACCESS_TYPES.OSS_GROUP,
                                "Type",
                                "accessGroup.agType",
                                listParameters?.filters ?? []
                            )
                        ),
                    })
                }
                id={masterId}
                lookup={extendLookups(AccessGroupLookup, 'accessGroup')}
                title={translate.byKey(TranslationKeys.assignedOssGroups)}>
                <Button variant="submit" shape="rounded" className="py-1 px-5" onClick={() => setShowManageModal(true)}>{translate.byKey(TranslationKeys.manage)}</Button>
            </AssignedTab>] : []),
            <AssignedTab
                key={"AccessProfile-tab"}
                title={translate.byKey(TranslationKeys.assignedAccessProfiles)}
                dataMethod={(listParameters?: ListQuery) => getAccessProfilesForPerson(masterId, listParameters)}
                column={AccessProfileColumn}
                searchColumn={extendSearchColumn(AccessProfileSearch, "accessProfile")}
                id={masterId}>
                <Button variant="submit" shape="rounded" className="py-1 px-5" onClick={() => setShowManageModal(true)}>{translate.byKey(TranslationKeys.manage)}</Button>
            </AssignedTab>
    ];

    const getAssignedItems = async (setSelectedIds: React.Dispatch<React.SetStateAction<string[]>>, setSelectedRows: React.Dispatch<React.SetStateAction<any[]>>, setOriginalSelectedIds: React.Dispatch<React.SetStateAction<string[]>>, setOriginalSelectedRows: React.Dispatch<React.SetStateAction<any[]>>, setBusyLoader: React.Dispatch<React.SetStateAction<boolean>>,setAlwaysSelectedAndDisabled?: React.Dispatch<React.SetStateAction<string[]>>) => {
        try {
            setBusyLoader(true);
            let ids: string[] = [], pageNumber = 1, pagination: PaginationModel, rows: any[] = [];
            
            if (beingAssigned === "ACCESS_GROUP" || beingAssigned === "FLOOR_GROUP" || beingAssigned === "OSS_GROUP") {
                const agType = beingAssigned === "ACCESS_GROUP" ? ACCESS_TYPES.ACCESS_GROUP : beingAssigned === "FLOOR_GROUP" ? ACCESS_TYPES.FLOOR_GROUP : ACCESS_TYPES.OSS_GROUP;
                const filters = appendFilter(agType, "Type", "accessGroup.agType", appendFilter(masterId, "Master", "master.id", []));

                do {
                    const masterGroups = await getAccessGroupsForPerson(translate, { filters, pageSize: 100, goToPage: pageNumber });
                    ids = ids.concat(masterGroups.rows.map(masterGroup => masterGroup.id.toString()));
                    rows = rows.concat(masterGroups.rows);
                    pagination = masterGroups.pagination;
                    pageNumber++;
                } while (pagination && pageNumber <= pagination.totalPages);

                const accessProfiles = (await getAccessProfilesForPerson(masterId)).rows;

                // Find all access groups that a linked with a access profiles 
                if (accessProfiles.length > 0) {
                    const accessProfilesIds: string[] = accessProfiles.map(accessGroup => accessGroup.id.toString());
                    const masterAccessGroupIds = await getAccessProfileAccessGroupsById(accessProfilesIds.join(','));
                    setAlwaysSelectedAndDisabled && setAlwaysSelectedAndDisabled(masterAccessGroupIds);
                }

                // Find all access groups that are linked with the master this includes access groups that are not linked with access profiles
                if (rows.length > 0) {
                    ids = rows.map(accessGroup => accessGroup.id.toString());
                }
            }
            else if (beingAssigned === "ACCESS_PROFILE") {
                do {
                    const accessProfiles = await getAccessProfilesForPerson(masterId, { pageSize: 100, goToPage: pageNumber });
                    ids = ids.concat(accessProfiles.rows.map(profile => profile.id.toString()));
                    rows = rows.concat(accessProfiles.rows);
                    pagination = accessProfiles.pagination;
                    pageNumber++;
                } while (pageNumber <= pagination.totalPages);
            }

            setSelectedIds(ids);
            setSelectedRows(rows);
            setOriginalSelectedIds(ids);
            setOriginalSelectedRows(rows);
        } catch (error) {
            showError(error, toast.warn);
        } finally {
            setBusyLoader(false);
        }
    }

    const handleSubmit = (toBeAssigned: string[], toBeUnassigned: string[]) => {
        setBusy(true);
        let responsePromises: Promise<unknown[]> = Promise.resolve([]);
        
        //Floor group is access group we should use same method to assign and unassign
        if (beingAssigned === "ACCESS_GROUP" || beingAssigned === "FLOOR_GROUP" || beingAssigned === "OSS_GROUP") {
            responsePromises = assignUnassignMasterGroup(toBeAssigned, toBeUnassigned, masterId)
        }
        else if (beingAssigned === "ACCESS_PROFILE") {
            responsePromises = assignAndUnassignMasterGroupProfile(toBeAssigned, toBeUnassigned, masterId, "MASTER");
        }

        responsePromises.then(() => {
            fetchAccessProfileAndGroupCounts(masterId);
        }).catch((error) => {
            dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(error) });
        }).finally(() => {
            setBusy(false);
        });
    }

    const onTabChange = (index: number) => {
        setSelectedTab(index);
        setBeingAssigned(getBeingAssigned(index));
    }
    
    const getBeingAssigned = (index: number): EntityType => {
        if (index == 0) {
            return "ACCESS_GROUP";
        } else if (index == 1) {
            return "FLOOR_GROUP";
        } else if (isLicensedForAperioOss && index == 2) {
            return "OSS_GROUP";
        }

        return "ACCESS_PROFILE";
    }

    const getAssigningTitle = (beingAssigned: EntityType) => {
        if (beingAssigned === "ACCESS_GROUP") {
            return translate.byKey(TranslationKeys.accessGroups);
        }
        else if (beingAssigned === "ACCESS_PROFILE") {
            return translate.byKey(TranslationKeys.accessProfiles);
        }
        else if (beingAssigned === "FLOOR_GROUP") {
            return translate.byKey(TranslationKeys.floorGroups);
        }
        return translate.byKey(TranslationKeys.ossGroups);
    }

    const getModalTitle = (beingAssigned: EntityType) => {
        if (beingAssigned === "ACCESS_GROUP") {
            return translate.byKey(TranslationKeys.assignAccessGroups);
        }
        else if (beingAssigned === "FLOOR_GROUP") {
            return translate.byKey(TranslationKeys.assignFloorGroups);
        }
        else if (beingAssigned === "ACCESS_PROFILE") {
            return translate.byKey(TranslationKeys.assignAccessProfiles);
        }
        return translate.byKey(TranslationKeys.assignOssGroups);
    }

    const getDataMethod = (beingAssigned: EntityType, listParameters?: ListQuery) => {
        if (beingAssigned === "ACCESS_GROUP") {
            return getAllAccessGroups(1, translate, listParameters)
        }
        else if (beingAssigned === "FLOOR_GROUP") {
            return getAllAccessGroups(2, translate, listParameters)
        }
        else if (beingAssigned === "ACCESS_PROFILE") {
            return getAllAccessProfiles(listParameters)
        }
        return getAllAccessGroups(3, translate, listParameters)
    }

    return <ActivityFrame busy={busy} className='flex flex-col flex-grow bg-white' >
        <div className="w-full overflow-hidden" >
            <Tabs
                tabs={tabs}
                onSelect={onTabChange}
                selected={selectedTab}
                add={false}
                onAdd={() => { }}
                isFlat={true}
                className="w-full"
                showBorder={false}>
                <hr />
                {tabContent[selectedTab]}
            </Tabs>
        </div>
        <AssignModalTwo
            assigningTitle={getAssigningTitle(beingAssigned)}
            dataMethod={(listParameters?: ListQuery) => getDataMethod(beingAssigned, listParameters)}
            getAssignedItems={getAssignedItems}
            isOpen={showManageModal}
            modalTitle={getModalTitle(beingAssigned)}
            onClose={() => setShowManageModal(false)}
            searchColumns={beingAssigned === "ACCESS_PROFILE" ?  AccessProfileSearch : AccessGroupSearch}
            tableColumns={beingAssigned === "ACCESS_PROFILE" ? AccessProfileColumn : beingAssigned === "OSS_GROUP" ? hideColumns(AccessGroupColumns, ['area', 'accessProfile', "days", "startTime", "duration", "enrolments"]) : hideColumns(AccessGroupColumns, ['accessProfile', "days", "startTime", "duration", "enrolments"])}
            lookup={beingAssigned === "ACCESS_PROFILE" ? [] : AccessGroupLookup} 
            handleSubmit={handleSubmit}/>
    </ActivityFrame>
}

export default UserDetailsAccessTabTwo;
